import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Stack, Button, Group, Text } from '@repletoryreach/sdk-ui';
import {
  Alert,
  EmptyState,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { RangePicker, rangeToSinceIso, type RangeOption } from '../audit/components/RangePicker';
import { FlowsTable } from './components/FlowsTable';
import { FlowsList } from './components/FlowsList';
import { useStatusLabel } from './components/RunStatusBadge';
import { FLOW_RUN_STATUSES, isInFlight, isStatus } from './lib/status';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { TableFooter } from '../../components/TableFooter';
import { usePersistedState } from '../../lib/usePersistedState';
import { useT } from '../../i18n/I18nProvider';

type KpiKey = 'all' | 'running' | 'completed' | 'failed' | 'handed_off';

// The service orders by StartedAt DESC and stops at this many rows. Reaching it
// means the window holds more runs than the console was sent, so the totals and
// the KPI counts describe the page, not the platform, and must say so.
const SERVER_ROW_CAP = 500;

export default function FlowsPage() {
  const t = useT();
  const statusLabel = useStatusLabel();
  const [range, setRange] = useState<RangeOption>('7d');
  const since = useMemo(() => rangeToSinceIso(range), [range]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['admin', 'audit', 'flow-runs', since],
    queryFn: () => admin.listFlowRuns(since)
  });
  const all = useMemo(() => data?.data ?? [], [data]);
  const capped = all.length >= SERVER_ROW_CAP;

  const [view, setView] = usePersistedState<ViewMode>('admin.flows.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.flows.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);

  const counts = useMemo(() => ({
    all: all.length,
    running:    all.filter((r) => isInFlight(r.status, r.finished_at)).length,
    completed:  all.filter((r) => isStatus(r.status, 'completed')).length,
    failed:     all.filter((r) => isStatus(r.status, 'failed')).length,
    handed_off: all.filter((r) => isStatus(r.status, 'handed_off')).length
  }), [all]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all',        label: t('flows.kpi.all'),           value: counts.all,        tone: 'primary' },
    { id: 'running',    label: t('status.running'),          value: counts.running,    tone: 'pending' },
    { id: 'completed',  label: t('status.completed'),        value: counts.completed,  tone: 'healthy' },
    { id: 'failed',     label: t('status.failed'),           value: counts.failed,     tone: 'down' },
    { id: 'handed_off', label: t('flows.status.handedOff'),  value: counts.handed_off, tone: 'warning' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    // Only offer statuses and flows the window actually holds, so a chosen
    // option can never return nothing.
    const statuses = FLOW_RUN_STATUSES.filter((s) => all.some((r) => r.status === s));
    const channels = Array.from(new Set(all.map((r) => r.channel).filter(Boolean))).sort();
    const flows = new Map<string, string>();
    for (const r of all) flows.set(r.flow_id, r.flow_name ?? t('flows.flowDeleted'));
    return [
      {
        key: 'status',
        label: t('common.status'),
        placeholder: t('flows.filters.statusPlaceholder'),
        options: statuses.map((s) => ({ value: s, label: statusLabel(s) }))
      },
      {
        key: 'flow_id',
        label: t('flows.columns.flow'),
        placeholder: t('flows.filters.flowPlaceholder'),
        options: Array.from(flows, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))
      },
      {
        key: 'channel',
        label: t('flows.columns.channel'),
        placeholder: t('flows.filters.channelPlaceholder'),
        options: channels.map((c) => ({ value: c, label: c }))
      }
    ];
  }, [all, t, statusLabel]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters = Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0 || range !== '7d';

  function clearAllFilters() {
    setQuery(''); setKpi('all'); setFilters({}); setRange('7d'); setPage(1);
  }

  // One pill per narrowing, each removable on its own. A count badge on the
  // Filters button says how many are on but not which, and the window counts
  // as a filter here because a 1h view hides most of what the page holds.
  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (kpi && kpi !== 'all') {
    pills.push({ id: 'kpi', field: t('common.status'), value: kpiItems.find((i) => i.id === kpi)?.label ?? kpi });
  }
  if (filters.status) pills.push({ id: 'status', field: t('common.status'), value: statusLabel(filters.status) });
  if (filters.flow_id) {
    const named = all.find((r) => r.flow_id === filters.flow_id)?.flow_name;
    pills.push({ id: 'flow_id', field: t('flows.columns.flow'), value: named ?? t('flows.flowDeleted') });
  }
  if (filters.channel) pills.push({ id: 'channel', field: t('flows.columns.channel'), value: filters.channel });
  if (range !== '7d') pills.push({ id: 'range', field: t('audit.range.label'), value: t(`audit.range.${range}`) });

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') return setQuery('');
    if (id === 'kpi') return setKpi('all');
    if (id === 'range') return setRange('7d');
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  const filtered = useMemo(() => {
    let rows = all;
    if (kpi === 'running')    rows = rows.filter((r) => isInFlight(r.status, r.finished_at));
    if (kpi === 'completed')  rows = rows.filter((r) => isStatus(r.status, 'completed'));
    if (kpi === 'failed')     rows = rows.filter((r) => isStatus(r.status, 'failed'));
    if (kpi === 'handed_off') rows = rows.filter((r) => isStatus(r.status, 'handed_off'));
    if (filters.status)  rows = rows.filter((r) => r.status === filters.status);
    if (filters.flow_id) rows = rows.filter((r) => r.flow_id === filters.flow_id);
    if (filters.channel) rows = rows.filter((r) => r.channel === filters.channel);
    const q = query.trim().toLowerCase();
    // A support ticket quotes the contact's number far more often than a run
    // id, so the number is the first thing this box has to match.
    if (q) rows = rows.filter((r) =>
      (r.contact_msisdn ?? '').toLowerCase().includes(q) ||
      (r.flow_name ?? '').toLowerCase().includes(q) ||
      r.id.toLowerCase().includes(q)
    );
    return rows;
  }, [all, kpi, filters, query]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.section.messaging')}
        title={t('flows.title')}
        description={t('flows.description')}
        actions={
          <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
            {t('action.refresh')}
          </Button>
        }
      />

      {/* The chips sit on the page ground under the header, where the design
          puts the shape of the data before the table that carries it. */}
      <KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />
      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <TableShell
        title={t('flows.table.title')}
        totalLabel={
          capped
            ? t('flows.table.totalCapped', { shown: filtered.length, total: all.length, range })
            : t('flows.table.total', { shown: filtered.length, total: all.length, range })
        }
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('flows.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <RangePicker value={range} onChange={(r) => { setRange(r); setPage(1); }} />
            <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
          </Group>
        }
        view={view}
        onViewChange={setView}
        availableViews={['table', 'list']}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <EmptyState
            title={hasActiveFilters ? t('flows.empty') : t('flows.emptyTitleNone')}
            description={hasActiveFilters ? t('flows.empty.filtered') : t('flows.empty.none')}
            action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
        error={error ? (
          <Alert color="error" title={t('flows.loadError')}>
            <Stack gap="sm" align="flex-start">
              <Text role="bodySmall">{(error as Error).message}</Text>
              <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>{t('action.retry')}</Button>
            </Stack>
          </Alert>
        ) : null}
        pagination={
          <Stack gap="sm" style={{ width: '100%' }}>
            {/* The cap belongs beside the pager, where a person reads how much
                of the platform this page is actually speaking for. */}
            {capped && !error && (
              <Alert color="info" variant="light">{t('flows.capNotice', { cap: SERVER_ROW_CAP, range })}</Alert>
            )}
            <TableFooter
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              capped={capped}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            />
          </Stack>
        }
      >
        {view === 'table' && <FlowsTable records={paged} loading={isLoading} />}
        {view === 'list'  && <FlowsList records={paged} />}
      </TableShell>
    </Stack>
  );
}
